import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { U } from "@/data/services";
import { useSettings } from "@/lib/SettingsContext";

const Counter: React.FC<{ target: number; trigger: boolean }> = ({ target, trigger }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    const start = performance.now(); let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / 1500);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, trigger]);
  return <>{val}<span className="iav-stat-suffix">+</span></>;
};

const HERO_BG = U("1470229722913-7c0e2dbbafd3", 2400, 88);
const STORY_IMG = U("1429962714451-bb934ecdc4ec", 1600, 88);
const VM_IMG = U("1516450360452-9312f5e86fc7", 1800, 88);

const About: React.FC = () => {
  useReveal();
  const { settings } = useSettings();
  const stats: [number, string][] = [[Number(settings.stats_events) || 500, "Events Delivered"], [Number(settings.stats_years) || 10, "Years of Excellence"], [Number(settings.stats_operations) || 2, "Global Operations"], [Number(settings.stats_services) || 9, "Core Services"]];
  const statsRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = statsRef.current; if (!el) return;
    const io = new IntersectionObserver((entries) => entries.forEach(e => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }), { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <main className="iav-page" data-testid="about-page">
      <section className="iav-page-hero iav-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="iav-page-hero-overlay" />
        <div className="iav-page-hero-inner">
          <span className="iav-tag-line" data-reveal>About Ideation</span>
          <h1 data-reveal>WHO<br /><span className="iav-accent">WE ARE</span></h1>
          <p className="lead" data-reveal>A technical production company headquartered in Dubai, with operations in India — delivering live event production across the Middle East and South Asia.</p>
        </div>
      </section>

      <section className="iav-section">
        <div className="iav-about-story">
          <div data-reveal className="iav-about-story-vis">
            <img src={STORY_IMG} alt="Ideation AV live event production" />
            <div className="iav-about-story-frame" aria-hidden />
          </div>
          <div className="iav-section-body iav-about-story-text" data-reveal>
            <span className="iav-tag-line">Our Story</span>
            <h2 className="iav-section-title">IDEATION<br /><span className="iav-accent">AUDIO VISUAL</span></h2>
            <p>Ideation Audio Visual is a technical production company born from a passion for transforming creative visions into reality. We specialise in delivering high-quality Audio, Lighting, LED, Video Mapping, Rigging, Staging and SFX solutions for live events across the Middle East and South Asia.</p>
            <p>With operations headquartered in Dubai and extending to India, we bring together seasoned engineers, creative technicians and production specialists who share one goal — flawless execution, every time.</p>
            <p>From intimate private gatherings to large-scale international concerts and government productions, Ideation is the invisible force that makes every event unforgettable.</p>
            <div className="iav-pills" data-testid="about-pills">
              <span className="iav-pill">Dubai HQ</span>
              <span className="iav-pill">India Ops</span>
              <span className="iav-pill">Live Events</span>
              <span className="iav-pill">Technical Excellence</span>
            </div>
            <div className="iav-cta-row">
              <Link to="/quote" className="iav-btn iav-btn-primary" data-cursor="cta">Get a Quote</Link>
              <Link to="/services" className="iav-btn iav-btn-outline" data-cursor="cta">View Services</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="iav-stats-row" ref={statsRef} data-testid="about-stats">
        {stats.map(([t, l]) => (
          <div key={l as string} className="iav-stat" data-reveal>
            <div className="iav-stat-num"><Counter target={t as number} trigger={inView} /></div>
            <div className="iav-stat-label">{l}</div>
          </div>
        ))}
      </section>

      <section className="iav-vm">
        <div className="iav-vm-vis">
          <img src={VM_IMG} alt="" />
          <div className="iav-vm-overlay" />
        </div>
        <div className="iav-vm-cont">
          <span className="iav-tag-line" data-reveal>Our Foundation</span>
          <div className="iav-vm-block" data-reveal>
            <span className="iav-vm-lbl">Vision</span>
            <div className="iav-vm-h">TRUSTED LEADER</div>
            <p>To be a trusted leader in event technical services, known for delivering innovative and reliable solutions that elevate every experience.</p>
          </div>
          <div className="iav-vm-block" data-reveal>
            <span className="iav-vm-lbl">Mission</span>
            <div className="iav-vm-h">FLAWLESS EXECUTION</div>
            <p>To provide seamless, high-quality technical services through expertise, advanced technology, and a commitment to excellence.</p>
          </div>
          <div className="iav-vm-pillars" data-reveal>
            <div className="iav-pillar">
              <span className="iav-pillar-ico" aria-hidden>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="6"/><path d="M12 6a6 6 0 0 1 6 6c0 2.5-1.5 4.5-3 5.5V20H9v-2.5C7.5 16.5 6 14.5 6 12a6 6 0 0 1 6-6z"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="9" y1="22" x2="15" y2="22"/></svg>
              </span>
              <div className="iav-pillar-name">Ideas</div>
              <p className="iav-pillar-txt">Every event begins with a vision we shape together.</p>
            </div>
            <div className="iav-pillar">
              <span className="iav-pillar-ico" aria-hidden>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              </span>
              <div className="iav-pillar-name">Innovation</div>
              <p className="iav-pillar-txt">New approaches enhancing creativity and impact.</p>
            </div>
            <div className="iav-pillar">
              <span className="iav-pillar-ico" aria-hidden>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07M8.46 8.46a5 5 0 0 0 0 7.07"/></svg>
              </span>
              <div className="iav-pillar-name">Technology</div>
              <p className="iav-pillar-txt">Advanced equipment, precision every time.</p>
            </div>
          </div>
          <div className="iav-cta-row" data-reveal>
            <Link to="/quote" className="iav-btn iav-btn-primary" data-cursor="cta">Get a Quote</Link>
            <Link to="/services" className="iav-btn iav-btn-outline" data-cursor="cta">View Services</Link>
          </div>
        </div>
      </section>
    </main>
  );
};
export default About;
